import { cn } from '@/lib/cn';

export type Step = {
  key: string;
  label: string;
};

type StepperProps = {
  steps: Step[];
  activeIndex: number;
  /** how far the user has progressed — anything ≤ this is considered "done" */
  completedThrough: number;
  onJump?: (index: number) => void;
};

export function Stepper({ steps, activeIndex, completedThrough, onJump }: StepperProps) {
  return (
    <ol className="flex items-center gap-3 mb-6 flex-wrap">
      {steps.map((step, i) => {
        const done = i < completedThrough;
        const active = i === activeIndex;
        const clickable = onJump && (done || i <= completedThrough);
        return (
          <li key={step.key} className="flex items-center gap-3">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onJump?.(i)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full text-[13px] font-semibold transition-colors',
                active && 'bg-primary text-white',
                !active && done && 'bg-success-bg text-success border border-emerald-200',
                !active && !done && 'bg-gray-100 text-muted',
                clickable && !active && 'hover:bg-primary-50 hover:text-primary cursor-pointer',
                !clickable && 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold',
                  active && 'bg-white/20 text-white',
                  !active && done && 'bg-success text-white',
                  !active && !done && 'bg-gray-200 text-muted',
                )}
              >
                {done ? '✓' : i + 1}
              </span>
              {step.label}
            </button>
            {i < steps.length - 1 && (
              <span className={cn('h-px w-6', done ? 'bg-success' : 'bg-border')} />
            )}
          </li>
        );
      })}
    </ol>
  );
}
